/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

import Header from '../components/Header';
import { CouponField } from '../components/CouponField';
import { Footer } from '../components/Footer';
import PaymentMethodSwitch, { type PaymentMethod } from '../components/PaymentMethodSwitch';
import PaymentsCards from '../components/PaymentsCard';
import PaymentsPix from '../components/PaymentsPix';
import TurnstileWidget from '../components/TurnstileWidget';
import { ChildService, type ChildFromApi } from '../services/child.service';
import { CouponService } from '../services/coupon.service';
import {
  avisoCupomForaDaForma,
  cupomDaForma,
  cupomSemFormaDisponivel,
  mensagemErroCupom,
} from '../utils/coupon-restrictions';
import { PlansService } from '../services/plans.service';
import { lookupCep } from '../services/address-lookup.service';
import {
  RenewalService,
  readClientIdFromRenewalToken,
  type RenewalChild,
  type RenewalData,
  type RenewalMode,
} from '../services/renewal.service';
import { applyMask } from '../utils/masks';
import { SKIP_TURNSTILE } from '../config/features';
import { isInApp, postToApp } from '../utils/inApp';
import {
  buildRenewalPlanOptions,
  buildUpgradePlanOptions,
  calcularEconomiaAnual,
  pixNaRenovacao,
  toRenewalPlanOption,
  type RenewalPlanOption,
} from '../utils/renewal-plans';
import { contratoDoPlano, formatarReais, ofertaDoPlano, termosDaAssinatura } from '../utils/termos-assinatura';

type Shipping = {
  cep: string;
  rua: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
};

type EditingCard = 'recipient' | 'children' | 'new-child' | 'shipping' | null;

const EMPTY_NEW_CHILD = { name: '', birthDate: '', gender: '' };

const EMPTY_SHIPPING: Shipping = {
  cep: '',
  rua: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
};

const formatPreco = (valor: number) => `R$ ${valor.toFixed(2).replace('.', ',')}`;

const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA',
  'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
];

/**
 * Dígitos do WhatsApp sem DDI/zero de operadora: `applyMask` pega os 11
 * PRIMEIROS dígitos, então "+55 11 98765-4321" virava "(55) 1 1987-6543" — um
 * número errado que passava na validação e ia pra sub, AppMax e CRM.
 */
const whatsappDigits = (value: string) => {
  let digits = (value || '').replace(/\D/g, '');
  if (digits.length >= 12 && digits.startsWith('55')) digits = digits.slice(2);
  if (digits.length >= 11 && digits.startsWith('0')) digits = digits.slice(1);
  return digits;
};

const maskWhatsapp = (value: string) => applyMask(whatsappDigits(value), 'phone');

/**
 * Problema do WhatsApp em texto pro cliente, ou null se estiver ok. 466 subs
 * encerradas em prod (23/09/2026) têm o número com 9 dígitos — sem DDD — e a
 * página travava o pagamento sem dizer por quê.
 */
const whatsappProblema = (value: string): string | null => {
  const digits = whatsappDigits(value);
  if (!digits) return 'Informe o WhatsApp com DDD.';
  if (digits.length < 10) return 'O WhatsApp está sem DDD. Informe o número completo, com DDD.';
  if (digits.length > 11 || digits.startsWith('0')) return 'Confira o WhatsApp: informe DDD + número.';
  return null;
};

/** Campos do endereço que faltam, na ordem do formulário. */
const faltasEndereco = (s: Shipping): string[] => {
  const faltas: string[] = [];
  if (s.cep.replace(/\D/g, '').length !== 8) faltas.push('CEP');
  if (!s.rua.trim()) faltas.push('rua');
  if (!s.numero.trim()) faltas.push('número');
  if (!s.bairro.trim()) faltas.push('bairro');
  if (!s.cidade.trim()) faltas.push('cidade');
  if (!UFS.includes(s.estado.trim().toUpperCase())) faltas.push('UF');
  return faltas;
};

const formatCep = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};

const formatBirthDate = (value: string) => {
  const iso = (value || '').split('T')[0];
  const [year, month, day] = iso.split('-');
  return day && month && year ? `${day}/${month}/${year}` : iso;
};

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

const joinNames = (names: string[]) => {
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
};

const SummaryCard = ({
  id,
  title,
  onEdit,
  editing,
  locked,
  incompleto,
  children,
}: {
  id?: string;
  title: string;
  onEdit?: () => void;
  editing?: boolean;
  /** Outro card está em edição: esconde o Editar (trocar descartaria o rascunho aberto). */
  locked?: boolean;
  /** Falta dado que trava o pagamento: borda vermelha. */
  incompleto?: boolean;
  children: ReactNode;
}) => (
  <section
    id={id}
    className={`bg-white rounded-2xl border p-4 lg:p-6 scroll-mt-4 ${
      incompleto && !editing ? 'border-red-300' : 'border-[#e6e9f0]'
    }`}
  >
    <div className="flex items-center justify-between gap-3 mb-3">
      <h2 className="font-bold text-base text-[#2C2F3A]">{title}</h2>
      {onEdit && !editing && !locked && (
        <button type="button" onClick={onEdit} className="text-[#018ED5] text-sm font-semibold">
          Editar
        </button>
      )}
    </div>
    {children}
  </section>
);

// Mesma paleta por família de plano do ChangePlanModal — o cliente reconhece o
// plano pela cor antes de ler o nome.
const planColor = (name: string) => {
  if (name === 'Descoberta') return '#018ED5';
  if (name === 'Explorador') return '#5BB947';
  if (name === 'Aventureiro') return '#7C2EA3';
  return '#0168AC';
};

/**
 * `billingCycle` sozinho mente: plano `monthly_annual` é cobrado por mês mas TEM
 * fidelidade de 12 meses — anunciar "cancele quando quiser" nele seria promessa
 * falsa no ato da escolha. Tipo de contrato e renovação pela régua dos termos
 * (utils/termos-assinatura); a multa não é afirmada aqui (ver `renovacao`).
 * Na web (`naWeb`), o texto de antes de 28/10/2026 — os termos longos ficam só
 * no app, que é o que a Google Play revisa.
 */
const describePlano = (plan: { billingCycle: string; planType: string | null }, naWeb = false) => {
  const contrato = contratoDoPlano(plan);
  if (naWeb) {
    if (contrato === 'anual') return 'Plano anual · fidelidade de 12 meses';
    if (contrato === 'mensal_12_meses') return 'Anual cobrado mês a mês · fidelidade de 12 meses';
    return 'Plano mensal · cancele quando quiser';
  }
  // Sem "renova automaticamente": no PIX à vista o anual NÃO renova sozinho.
  // A renovação por forma de pagamento fica no quadro de termos.
  if (contrato === 'anual') return 'Plano anual · contrato de 12 meses';
  // Anual pago por mês (recorrente/parcelado): chamar de "mensal" confundia —
  // é contrato de 12 meses.
  if (contrato === 'mensal_12_meses') {
    return 'Cobrado mês a mês · contrato de 12 meses, renova automaticamente';
  }
  return 'Plano mensal · renova todo mês · cancele quando quiser';
};

const PlanOption = ({
  plan,
  selected,
  atual,
  economia,
  onSelect,
}: {
  plan: RenewalPlanOption;
  selected: boolean;
  atual: boolean;
  economia: number | null;
  onSelect: () => void;
}) => {
  // No app, valor COBRADO em destaque (o ano no anual), parcela abaixo (recusa
  // da Google Play, 28/10/2026). Na web, o ÷12 "/mês" de antes.
  const naWeb = !isInApp();
  const oferta = ofertaDoPlano(
    {
      billingCycle: plan.billingCycle,
      planType: plan.planType,
      precoTabela: plan.discountedPrice,
      precoOriginal: plan.originalPrice,
    },
    { anualPorMes: naWeb },
  );
  const cor = planColor(plan.name);

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`w-full text-left rounded-xl border p-3 transition-all ${
        selected ? 'border-[#018ED5] bg-[#F5FAFF] shadow-sm' : 'border-[#e6e9f0] bg-white'
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-1 w-5 h-5 shrink-0 rounded-full border-2 flex items-center justify-center ${
            selected ? 'border-[#018ED5]' : 'border-[#dadae5]'
          }`}
        >
          {selected && <span className="w-2.5 h-2.5 rounded-full bg-[#018ED5]" />}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-bold truncate" style={{ color: cor }}>
              {plan.name}
            </span>
            <span className="font-black text-[#2C2F3A] whitespace-nowrap">
              {oferta.preco}
              <span className="font-normal text-sm text-[#595D69]"> {oferta.sufixo}</span>
            </span>
          </div>

          <div className="flex items-center justify-between gap-2 mt-0.5">
            <span className="text-xs text-[#595D69]">{describePlano(plan, naWeb)}</span>
            {oferta.precoOriginal && (
              <span className="text-xs text-[#9195A1] line-through whitespace-nowrap">{oferta.precoOriginal}</span>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mt-2">
            {atual && (
              <span className="text-[11px] font-semibold text-[#0168AC] bg-[#DBECFF] rounded-full px-2 py-0.5">
                Seu plano atual
              </span>
            )}
            {economia !== null && (
              <span className="text-[11px] font-semibold text-[#2F6B22] bg-[#f6fcf1] rounded-full px-2 py-0.5">
                Economize {formatPreco(economia)} por ano
              </span>
            )}
            {oferta.parcela && (
              <span className={naWeb ? 'text-[11px] text-[#595D69]' : 'text-[11px] font-semibold text-[#2C2F3A]'}>
                {oferta.parcela}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

const Field = ({
  label,
  value,
  onChange,
  placeholder,
  inputMode,
  maxLength,
  type,
  autoComplete,
  min,
  max,
  invalid,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  inputMode?: 'text' | 'numeric' | 'tel';
  maxLength?: number;
  type?: string;
  autoComplete?: string;
  min?: string;
  max?: string;
  invalid?: boolean;
}) => (
  <label className="flex flex-col gap-1 w-full">
    <span className="text-sm text-[#595D69]">{label}</span>
    <input
      className={`border rounded-lg h-12 px-4 w-full ${
        invalid ? 'border-red-400 bg-red-50' : 'border-[#dadae5]'
      }`}
      value={value}
      placeholder={placeholder}
      inputMode={inputMode}
      maxLength={maxLength}
      type={type}
      autoComplete={autoComplete}
      min={min}
      max={max}
      aria-invalid={invalid || undefined}
      onChange={(event) => onChange(event.target.value)}
    />
  </label>
);

/** Mensagem que o backend devolveu (ex.: validação de data), ou o fallback. */
const mensagemDaApi = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string | string[] } | undefined)?.message;
    if (Array.isArray(msg) && msg.length) return msg[0];
    if (typeof msg === 'string' && msg) return msg;
  }
  return fallback;
};

const HOJE = new Date().toISOString().split('T')[0];

const Renewal = () => {
  const { token: tokenDaUrl, code } = useParams<{ token?: string; code?: string }>();
  // Link curto (/r/:code): o token só existe depois que a hidratação troca o
  // código. Daí em diante (dono, pagamento) tudo usa este.
  const [token, setToken] = useState(tokenDaUrl ?? '');

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorTitle, setErrorTitle] = useState('Link indisponível');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorRetry, setErrorRetry] = useState(false);

  // Estado só em memória: refresh re-hidrata pelo token (decisão histórica do
  // checkout — localStorage de fluxo já causou bugs de dados cruzados).
  const [data, setData] = useState<RenewalData | null>(null);
  const [clientId, setClientId] = useState('');
  const [planos, setPlanos] = useState<RenewalPlanOption[]>([]);
  const [planId, setPlanId] = useState('');
  const [email, setEmail] = useState('');
  const [recipient, setRecipient] = useState({ name: '', whatsapp: '' });
  const [shipping, setShipping] = useState<Shipping>(EMPTY_SHIPPING);
  const [children, setChildren] = useState<RenewalChild[]>([]);
  // Crianças "órfãs" do cadastro (sem sub vinculada — subs pré-refactor):
  // oferecidas pra INCLUIR quando a assinatura não tem nenhuma vinculada.
  const [suggestedChildren, setSuggestedChildren] = useState<RenewalChild[]>([]);
  const [newChild, setNewChild] = useState(EMPTY_NEW_CHILD);
  const [savingNewChild, setSavingNewChild] = useState(false);
  const [coupon, setCoupon] = useState<any>(null);
  const [couponInput, setCouponInput] = useState('');
  const [couponError, setCouponError] = useState(false);
  const [couponErrorMessage, setCouponErrorMessage] = useState<string | null>(null);
  // #512: `código|plano|formas` da última validação que passou — trocar de
  // plano (ou de formas oferecidas) revalida o cupom aplicado.
  const cupomValidadoPara = useRef<string | null>(null);
  // Cupom travado no link (Black Friday nov/2026): aplicado na hidratação, sem
  // campo para trocar/remover e sem revalidar no `/coupons/validate` (que o
  // recusa por ser só-por-link). O backend cobra com ele de qualquer jeito.
  const [cupomTravado, setCupomTravado] = useState(false);
  // Crianças do cadastro incluídas nesta tela: ainda não vinculadas (o vínculo
  // é na aprovação), então dá pra desfazer.
  const [incluidasDaSugestao, setIncluidasDaSugestao] = useState<string[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('credit-card');
  const [turnstileToken, setTurnstileToken] = useState('');

  const [editing, setEditing] = useState<EditingCard>(null);
  const [recipientDraft, setRecipientDraft] = useState({ name: '', whatsapp: '' });
  const [shippingDraft, setShippingDraft] = useState<Shipping>(EMPTY_SHIPPING);
  const [childrenDraft, setChildrenDraft] = useState<RenewalChild[]>([]);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepNotFound, setCepNotFound] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [savingChildren, setSavingChildren] = useState(false);
  // "Completar cadastro": abre sozinho, em sequência, cada card que trava o
  // pagamento. Para quando o cliente cancela uma edição.
  const completandoCadastro = useRef(false);
  // CEP que já estava no editor ao abrir: não re-consulta (a consulta
  // sobrescrevia rua/bairro digitados à mão com o ViaCEP).
  const cepAoAbrir = useRef('');

  useEffect(() => {
    let cancelled = false;

    const fail = (title: string, message: string, retry: boolean) => {
      if (cancelled) return;
      setErrorTitle(title);
      setErrorMessage(message);
      setErrorRetry(retry);
      setStatus('error');
    };

    const describe = (error: unknown): { title: string; message: string; retry: boolean } => {
      if (axios.isAxiosError(error)) {
        const httpStatus = error.response?.status;
        const body = error.response?.data as
          | { message?: string; reason?: string }
          | undefined;
        const message = body?.message;
        const reason = body?.reason;

        if (httpStatus === 410) {
          return {
            title: 'Link expirado',
            message: message || 'Este link de renovação expirou.',
            retry: false,
          };
        }
        if (httpStatus === 409) {
          // Reasons da régua de UPGRADE: o título de renovação ("já está
          // ativa") mentiria aqui — a mensagem do backend explica o caso.
          const reasonsUpgrade = [
            'assinatura_nao_ativa',
            'anual_ativo',
            'sem_plano_superior',
          ];
          if (reason && reasonsUpgrade.includes(reason)) {
            return {
              title: 'Upgrade indisponível',
              message:
                message || 'Não foi possível fazer o upgrade desta assinatura.',
              retry: false,
            };
          }
          return {
            title: 'Assinatura já está ativa',
            message: message || 'Esta assinatura já está ativa — não há o que renovar.',
            retry: false,
          };
        }
        if (httpStatus && httpStatus < 500) {
          return {
            title: 'Link inválido',
            message: message || 'Este link de renovação não é mais válido.',
            retry: false,
          };
        }
        return {
          title: 'Não foi possível carregar',
          message: 'Tivemos um problema ao carregar seus dados. Tente novamente em instantes.',
          retry: true,
        };
      }

      if (error instanceof Error && error.message === 'plano-indisponivel') {
        return {
          title: 'Plano indisponível',
          message:
            'Não conseguimos carregar o plano desta assinatura. Fale com o suporte pelo aplicativo.',
          retry: false,
        };
      }

      if (error instanceof Error && error.message === 'catalogo-indisponivel') {
        return {
          title: 'Não foi possível carregar',
          message:
            'Não conseguimos carregar os planos disponíveis para o upgrade. Tente novamente em instantes.',
          retry: true,
        };
      }

      if (error instanceof Error && error.message === 'sem-plano-superior') {
        return {
          title: 'Upgrade indisponível',
          message:
            'Você já está no nosso melhor plano — não há upgrade disponível no momento.',
          retry: false,
        };
      }

      return {
        title: 'Não foi possível carregar',
        message: 'Tivemos um problema ao carregar seus dados. Tente novamente em instantes.',
        retry: true,
      };
    };

    async function hydrate() {
      if (!tokenDaUrl && !code) {
        fail('Link inválido', 'Este link de renovação não é válido.', false);
        return;
      }

      setStatus('loading');
      try {
        let tokenDoLink = tokenDaUrl ?? '';
        let cupomDoLink: string | null = null;
        if (!tokenDoLink && code) {
          // 404 de código desconhecido cai no describe() como "Link inválido".
          const curto = await RenewalService.resolveShortLink(code);
          tokenDoLink = curto.token;
          cupomDoLink = curto.cupom;
        }
        if (!cancelled) setToken(tokenDoLink);

        const renewal = await RenewalService.getByToken(tokenDoLink);
        const owner = readClientIdFromRenewalToken(tokenDoLink);
        if (!owner) throw new Error('token-sem-dono');

        const planoAtual = toRenewalPlanOption(renewal.plan);
        if (!planoAtual) throw new Error('plano-indisponivel');

        const upgradeFlow = renewal.purpose === 'upgrade';

        // Oferta fechada: o link só paga este plano. Na renovação vem da
        // automação; no upgrade, do painel (campanha mensal → anual).
        const planoOfertado = toRenewalPlanOption(renewal.offerPlan);

        // Catálogo é OPCIONAL na renovação: se o GET /plans falhar, ela continua
        // de pé com o plano atual — perder a escolha de plano é melhor do que
        // perder a renovação inteira. No UPGRADE aberto é OBRIGATÓRIO: sem
        // catálogo não há o que comprar (o plano atual não é opção). Com a
        // oferta fechada o plano já veio no link.
        let catalogo: unknown = [];
        try {
          catalogo = await PlansService.getAll();
        } catch {
          if (upgradeFlow && !planoOfertado) throw new Error('catalogo-indisponivel');
          catalogo = [];
        }

        const opcoes = upgradeFlow
          ? buildUpgradePlanOptions(catalogo, planoAtual, planoOfertado)
          : buildRenewalPlanOptions(catalogo, planoAtual, planoOfertado);
        // A emissão do link já recusa `sem_plano_superior`, mas o catálogo pode
        // ter mudado entre a emissão e a abertura da página.
        if (upgradeFlow && opcoes.length === 0) throw new Error('sem-plano-superior');

        if (cancelled) return;

        setData(renewal);
        setClientId(owner);
        setPlanos(opcoes);
        // Upgrade: pré-seleciona o MENOR plano superior (lista já ordenada).
        const planoInicial = upgradeFlow || planoOfertado ? opcoes[0].id : planoAtual.id;
        setPlanId(planoInicial);
        setEmail(renewal.email ?? '');
        setRecipient({
          name: renewal.recipient.name ?? '',
          whatsapp: maskWhatsapp(renewal.recipient.whatsapp ?? ''),
        });
        setShipping({
          cep: formatCep(renewal.shipping.zip ?? ''),
          rua: renewal.shipping.street ?? '',
          numero: renewal.shipping.number ?? '',
          complemento: renewal.shipping.complement ?? '',
          bairro: renewal.shipping.neighborhood ?? '',
          cidade: renewal.shipping.city ?? '',
          estado: (renewal.shipping.state ?? '').trim().toUpperCase(),
        });
        setChildren(
          renewal.children.map((child) => ({
            ...child,
            birthDate: (child.birthDate || '').split('T')[0],
          })),
        );

        // Sub sem criança vinculada (585 ativas em prod, 24/08): oferecer as
        // órfãs do cadastro pra incluir em vez de travar o pagamento. Só
        // órfãs (subscriptionId null) — criança de OUTRA sub fica fora, senão
        // o vínculo na aprovação a roubaria da assinatura irmã. Falha aqui é
        // silenciosa: a sugestão é conveniência, o cadastro manual cobre.
        if (renewal.children.length === 0) {
          try {
            const doCadastro = await ChildService.getByClientId(owner, tokenDoLink);
            if (!cancelled) {
              setSuggestedChildren(
                doCadastro
                  .filter((child: ChildFromApi) => !child.subscriptionId)
                  .map((child: ChildFromApi) => ({
                    id: child.id,
                    name: child.name,
                    birthDate: (child.birthdate || '').split('T')[0],
                    gender: child.gender ?? null,
                  })),
              );
            }
          } catch {
            /* sugestão é opcional */
          }
        }
        completandoCadastro.current = true;
        setStatus('ready');

        // Cupom travado no token: vale ele, e nenhum outro da URL ou do apelido.
        if (renewal.cupomTravado) {
          setCoupon(renewal.cupomTravado);
          setCupomTravado(true);
          return;
        }

        // Link curto: o cupom da oferta vem gravado junto do código, não na URL.
        const params = new URLSearchParams(window.location.search);
        const couponFromUrl = params.get('coupon') || params.get('cupom') || cupomDoLink;
        if (couponFromUrl) {
          try {
            const validated = await CouponService.validate(couponFromUrl.toUpperCase(), planoInicial);
            if (!cancelled) setCoupon(validated);
          } catch (error) {
            // Cupom do link (oferta da IA) que não vale mais: mostra no campo com
            // o erro em vez de sumir em silêncio.
            if (!cancelled) {
              setCouponInput(couponFromUrl.toUpperCase());
              setCouponErrorMessage(mensagemErroCupom(error));
              setCouponError(true);
            }
          }
        }
      } catch (error) {
        const described = describe(error);
        fail(described.title, described.message, described.retry);
      }
    }

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [tokenDaUrl, code]);

  const planoSelecionado = useMemo(
    () => planos.find((plan) => plan.id === planId) ?? null,
    [planos, planId],
  );
  const isUpgrade = data?.purpose === 'upgrade';
  const trocouDePlano = !!planoSelecionado && planoSelecionado.id !== data?.plan.id;

  // Espelha a regra do servidor (resolveEffectiveRenewalMode): manter o plano de
  // uma assinatura mensal retoma a MESMA assinatura; qualquer troca vira contrato
  // novo. Upgrade é refazer por definição (o plano atual nem está no seletor).
  // Só afeta a copy — quem decide de verdade é o backend.
  // Plano anual à vista nunca é "retomar", mesmo sendo o plano da própria
  // assinatura: quem paga o ano recebe a assinatura anual nova.
  const planoAnualAVista =
    !!planoSelecionado && pixNaRenovacao(planoSelecionado) === 'anual_a_vista';
  const modoEfetivo: RenewalMode =
    !isUpgrade && !trocouDePlano && !planoAnualAVista && data?.mode === 'retomar'
      ? 'retomar'
      : 'refazer';

  const economias = useMemo(() => {
    const mapa = new Map<string, number | null>();
    for (const plan of planos) mapa.set(plan.id, calcularEconomiaAnual(plan, planos));
    return mapa;
  }, [planos]);

  const isAnual = planoSelecionado?.billingCycle === 'annual';
  // PIX segue o plano ESCOLHIDO (mesma régua do backend): mensal puro paga o
  // mês; anual à vista paga o valor TOTAL do ano de uma vez.
  const pixMode = planoSelecionado ? pixNaRenovacao(planoSelecionado) : null;
  // O PIX à vista só existe quando nasce a assinatura anual nova ("refazer").
  // Num "retomar" em plano anual (assinatura mensal legada apontando pra plano
  // à vista) o servidor recusa — então nem oferecemos o botão.
  const allowsPix =
    pixMode === 'mensal' || (pixMode === 'anual_a_vista' && modoEfetivo === 'refazer');
  const pixAVista = allowsPix && pixMode === 'anual_a_vista' && paymentMethod === 'pix';
  // #128: exigência de Turnstile vem do plano (plans.requires_turnstile), não do slug.
  const requireTurnstile = planoSelecionado?.requiresTurnstile === true && !SKIP_TURNSTILE;

  // #512: o cupom que vale NESTA forma de pagamento — o só-PIX na aba do
  // cartão (ou o inverso) fica fora do preço e do POST, com aviso.
  const cupomEfetivo = cupomDaForma(coupon, paymentMethod);
  const avisoCupom = avisoCupomForaDaForma(coupon, paymentMethod);

  const precoTabela = Number(planoSelecionado?.discountedPrice ?? 0) || 0;
  const precoFinalNumber = useMemo(() => {
    if (!cupomEfetivo) return precoTabela;
    return cupomEfetivo.discount_type === 'PERCENT'
      ? precoTabela * (1 - cupomEfetivo.value / 100)
      : Math.max(0, precoTabela - cupomEfetivo.value);
  }, [cupomEfetivo, precoTabela]);
  const precoMensal = isAnual ? precoFinalNumber / 12 : precoFinalNumber;
  // Cupom no mensal: no cartão vale só a 1ª cobrança (o cron cobra o cheio);
  // no PIX fica em todo mês (paidAmount). Mesma régua de utils/termos-assinatura.
  // Termos e avisos de cobrança: só dentro do app (o que a Google Play revisa).
  // Na web, o resumo de antes de 28/10/2026 — o ano em destaque derrubou a
  // conversão do checkout (28–29/10).
  const termosNaTela = isInApp();
  const cupomSoNoPrimeiroMes =
    termosNaTela && !isAnual && paymentMethod === 'credit-card' && precoTabela - precoFinalNumber > 0.005;
  // Anual é cobrado em 12x no cartão: sem isto a barra do celular mostrava só
  // "R$ 69,90", sem dizer que é por mês nem quantas vezes. No PIX à vista o
  // valor em destaque é o que o cliente paga agora: o ano inteiro.
  const precoFinal = formatPreco(pixAVista ? precoFinalNumber : precoMensal);
  const detalhePreco = pixAVista
    ? 'à vista no PIX · vale por 12 meses'
    : isAnual
      ? `por mês · 12x no cartão = ${formatPreco(precoFinalNumber)}`
      : 'por mês';
  // Termos da assinatura (mesma régua do checkout, utils/termos-assinatura):
  // quadro acima do botão de pagar e a barra do celular. Com `renovacao` a
  // multa sai condicional ("se ainda não completou 12 kits"): ela só vale no
  // 1º ciclo (`cicloCorrente` = 0), e o contrato novo herda a sequência de kits
  // da assinatura anterior (renewal-mode.ts). A página não tem o nº do kit.
  // `modoRenovacao`: no PIX do "retomar" o próximo PIX sai do paidAmount antigo
  // (applyRenewalTakeover não o regrava) — o texto não promete valor futuro.
  const termos =
    planoSelecionado && termosNaTela
      ? termosDaAssinatura({
          plano: planoSelecionado,
          forma: paymentMethod,
          precoTabela,
          precoComCupom: precoFinalNumber,
          renovacao: true,
          modoRenovacao: modoEfetivo,
        })
      : undefined;
  // Mesmo caso no resumo do topo: PIX mensal do "retomar" é só "este PIX".
  const pixSemValorFuturo = termosNaTela && !isAnual && paymentMethod === 'pix' && modoEfetivo !== 'refazer';

  useEffect(() => {
    if (!allowsPix && paymentMethod === 'pix') setPaymentMethod('credit-card');
  }, [allowsPix, paymentMethod]);

  // #512: trocar de plano com o cupom aplicado revalida o cupom (o tipo do
  // plano pode não ser aceito), e cupom só numa forma que o plano escolhido não
  // oferece (só PIX num plano só-cartão) sai com a mensagem em vez de ficar
  // aplicado sem valer em lugar nenhum.
  useEffect(() => {
    // Travado no link: o plano é fixo (oferta fechada) e o `/coupons/validate`
    // recusaria o cupom só-por-link — quem confere é o POST /payments.
    if (!coupon || !planId || cupomTravado) return;
    const formas: PaymentMethod[] = allowsPix ? ['credit-card', 'pix'] : ['credit-card'];
    const chave = `${coupon.code}|${planId}|${formas.join(',')}`;
    if (cupomValidadoPara.current === chave) return;

    const recusar = (mensagem: string) => {
      setCoupon(null);
      setCouponInput(coupon.code);
      setCouponErrorMessage(mensagem);
      setCouponError(true);
    };
    let cancelado = false;
    CouponService.validate(coupon.code, planId).then(
      (validado) => {
        if (cancelado) return;
        const inutilizavel = cupomSemFormaDisponivel(validado, formas);
        if (inutilizavel) return recusar(inutilizavel);
        cupomValidadoPara.current = chave;
        setCoupon(validado);
      },
      (error) => {
        if (!cancelado) recusar(mensagemErroCupom(error));
      },
    );
    return () => {
      cancelado = true;
    };
  }, [coupon, planId, allowsPix, cupomTravado]);

  useEffect(() => {
    const digits = shippingDraft.cep.replace(/\D/g, '');
    if (editing !== 'shipping' || digits.length !== 8) return;
    if (digits === cepAoAbrir.current) return;
    cepAoAbrir.current = digits;

    let cancelled = false;
    setCepLoading(true);
    setCepNotFound(false);
    lookupCep(digits)
      .then((result) => {
        if (cancelled) return;
        if (!result.found) {
          setCepNotFound(true);
          return;
        }
        // CEP novo = endereço novo: número e complemento do anterior não valem.
        setShippingDraft((prev) => ({
          ...prev,
          rua: result.rua || '',
          numero: '',
          complemento: '',
          bairro: result.bairro || '',
          cidade: result.cidade || prev.cidade,
          estado: result.estado || prev.estado,
        }));
      })
      .finally(() => {
        if (!cancelled) setCepLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [shippingDraft.cep, editing]);

  const problemaWhatsapp = whatsappProblema(recipient.whatsapp);
  const nomeOk = recipient.name.trim().length > 2;
  const responsavelCompleto = nomeOk && !problemaWhatsapp;
  const faltasDoEndereco = faltasEndereco(shipping);
  const enderecoCompleto = faltasDoEndereco.length === 0;
  const dadosCompletos = responsavelCompleto && enderecoCompleto && children.length > 0;

  // Dentro da WebView do app: rodapé institucional (termos/versão/copyright)
  // é ruído — o container é o próprio app. No navegador ele fica.
  const inApp = isInApp();
  // Quem chega pelo link do WhatsApp não usa o app: "abra o aplicativo" era
  // beco sem saída.
  const pecaNovoLink = inApp
    ? 'Gere um novo link de renovação no aplicativo.'
    : 'Peça um novo link de renovação na conversa do WhatsApp.';
  const falarComEquipe = inApp ? 'fale com o suporte pelo aplicativo' : 'fale com a nossa equipe pelo WhatsApp';

  const voltarAoAplicativo = () => {
    postToApp({ type: 'renewal-close' });
    window.close();
  };

  const rolarAte = (id: string) => {
    // Depois do render do editor aberto.
    requestAnimationFrame(() =>
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
  };

  const cancelarEdicao = () => {
    completandoCadastro.current = false;
    setCardError(null);
    setEditing(null);
  };

  const openRecipientEdit = () => {
    setRecipientDraft({ ...recipient });
    // Abre já apontando o problema (ex.: número sem DDD vindo do cadastro).
    setCardError(!nomeOk ? 'Informe o nome completo do responsável.' : problemaWhatsapp);
    setEditing('recipient');
  };

  const saveRecipient = () => {
    if (recipientDraft.name.trim().length < 3) {
      setCardError('Informe o nome completo do responsável.');
      return;
    }
    const problema = whatsappProblema(recipientDraft.whatsapp);
    if (problema) {
      setCardError(problema);
      return;
    }
    setRecipient({ name: recipientDraft.name.trim(), whatsapp: maskWhatsapp(recipientDraft.whatsapp) });
    setCardError(null);
    setEditing(null);
  };

  const openShippingEdit = () => {
    setShippingDraft({ ...shipping });
    cepAoAbrir.current = shipping.cep.replace(/\D/g, '');
    setCepNotFound(false);
    setCardError(
      faltasDoEndereco.length > 0 && shipping.cep
        ? `Falta preencher: ${faltasDoEndereco.join(', ')}.`
        : null,
    );
    setEditing('shipping');
  };

  const saveShipping = () => {
    const faltas = faltasEndereco(shippingDraft);
    if (faltas.length > 0) {
      setCardError(`Falta preencher: ${faltas.join(', ')}.`);
      return;
    }
    setShipping({
      ...shippingDraft,
      cep: formatCep(shippingDraft.cep),
      estado: shippingDraft.estado.trim().toUpperCase(),
    });
    setCardError(null);
    setEditing(null);
  };

  const openChildrenEdit = () => {
    setChildrenDraft(children.map((child) => ({ ...child })));
    setCardError(null);
    setEditing('children');
  };

  const saveChildren = async () => {
    const invalid = childrenDraft.find(
      (child) => child.name.trim().length < 3 || !child.birthDate,
    );
    if (invalid) {
      setCardError('Preencha nome e data de nascimento de cada criança.');
      return;
    }

    setSavingChildren(true);
    try {
      const alterados = childrenDraft.filter((child) => {
        const original = children.find((item) => item.id === child.id);
        return (
          !original ||
          original.name !== child.name ||
          original.birthDate !== child.birthDate ||
          original.gender !== child.gender
        );
      });

      await Promise.all(
        alterados.map((child) =>
          ChildService.update(child.id, {
            name: child.name.trim(),
            birthdate: child.birthDate,
            gender: child.gender || undefined,
            clientId,
          }, token),
        ),
      );

      setChildren(childrenDraft.map((child) => ({ ...child, name: child.name.trim() })));
      setCardError(null);
      setEditing(null);
    } catch (error) {
      setCardError(mensagemDaApi(error, 'Não foi possível salvar os dados das crianças. Tente novamente.'));
    } finally {
      setSavingChildren(false);
    }
  };

  // Incluir criança órfã do cadastro: só entra na lista local — o vínculo real
  // com a assinatura NOVA acontece na aprovação, via childIds → pending_child_ids.
  const includeSuggestedChild = (child: RenewalChild) => {
    setSuggestedChildren((prev) => prev.filter((item) => item.id !== child.id));
    setChildren((prev) => [...prev, child]);
    setIncluidasDaSugestao((prev) => [...prev, child.id]);
  };

  const undoSuggestedChild = (child: RenewalChild) => {
    setChildren((prev) => prev.filter((item) => item.id !== child.id));
    setSuggestedChildren((prev) => [...prev, child]);
    setIncluidasDaSugestao((prev) => prev.filter((id) => id !== child.id));
  };

  const openNewChild = () => {
    setNewChild(EMPTY_NEW_CHILD);
    setCardError(null);
    setEditing('new-child');
  };

  const saveNewChild = async () => {
    if (newChild.name.trim().length < 3 || !newChild.birthDate) {
      setCardError('Preencha nome e data de nascimento da criança.');
      return;
    }
    setSavingNewChild(true);
    try {
      const created = await ChildService.create({
        name: newChild.name.trim(),
        birthdate: newChild.birthDate,
        gender: newChild.gender || undefined,
        clientId,
      }, token);
      setChildren((prev) => [
        ...prev,
        {
          id: created.id,
          name: created.name ?? newChild.name.trim(),
          // Data DIGITADA, não a do retorno: o backend devolve birthdate como
          // timestamp e o shift UTC−3 mostraria o dia anterior na tela.
          birthDate: newChild.birthDate,
          gender: created.gender ?? (newChild.gender || null),
        },
      ]);
      setCardError(null);
      setEditing(null);
    } catch (error) {
      setCardError(mensagemDaApi(error, 'Não foi possível cadastrar a criança. Tente novamente.'));
    } finally {
      setSavingNewChild(false);
    }
  };

  const aplicarCupom = async () => {
    const codigo = couponInput.trim().toUpperCase();
    if (!codigo) return;
    try {
      // O efeito de revalidação confere as formas oferecidas pelo plano.
      setCoupon(await CouponService.validate(codigo, planId || undefined));
      setCouponError(false);
      setCouponErrorMessage(null);
    } catch (error) {
      setCoupon(null);
      setCouponErrorMessage(mensagemErroCupom(error));
      setCouponError(true);
    }
  };

  const removerCupom = () => {
    setCoupon(null);
    setCouponInput('');
    setCouponError(false);
    setCouponErrorMessage(null);
  };

  // Tudo que trava o pagamento, de uma vez (antes aparecia uma pendência por
  // vez e o telefone sem DDD nem era citado). `abrir` null = não há editor pra
  // abrir sozinho (criança sugerida do cadastro: o cliente escolhe incluir).
  const pendencias: Array<{ chave: string; texto: string; cardId: string; abrir: (() => void) | null }> = [];
  if (!responsavelCompleto) {
    pendencias.push({
      chave: 'responsavel',
      texto: !nomeOk ? 'Nome do responsável' : 'WhatsApp com DDD',
      cardId: 'card-responsavel',
      abrir: openRecipientEdit,
    });
  }
  if (children.length === 0) {
    pendencias.push({
      chave: 'criancas',
      texto: 'Criança que vai receber os kits',
      cardId: 'card-criancas',
      abrir: suggestedChildren.length > 0 ? null : openNewChild,
    });
  }
  if (!enderecoCompleto) {
    const temAlgum = shipping.cep || shipping.rua;
    pendencias.push({
      chave: 'endereco',
      texto: temAlgum
        ? `Endereço de entrega (falta ${faltasDoEndereco.join(', ')})`
        : 'Endereço de entrega',
      cardId: 'card-endereco',
      abrir: openShippingEdit,
    });
  }
  const chavePendencias = pendencias.map((p) => p.chave).join('|');
  const primeiraAbertura = useRef(true);

  useEffect(() => {
    if (status !== 'ready' || editing !== null || !completandoCadastro.current) return;
    const proxima = pendencias.find((p) => p.abrir);
    if (!proxima) {
      completandoCadastro.current = false;
      return;
    }
    proxima.abrir!();
    // Na chegada o topo (plano e preço) fica à vista; nas seguintes, rola até o card.
    if (!primeiraAbertura.current) rolarAte(proxima.cardId);
    primeiraAbertura.current = false;
    // `pendencias` é recalculada a cada render; a chave captura o que importa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, editing, chavePendencias]);

  const resolverPendencia = (pendencia: (typeof pendencias)[number]) => {
    completandoCadastro.current = true;
    if (pendencia.abrir) pendencia.abrir();
    rolarAte(pendencia.cardId);
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#f6fafe] flex flex-col items-center justify-center gap-4 px-6">
        <svg className="animate-spin h-8 w-8 text-[#018ED5]" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
        <p className="text-[#595D69] text-center">Carregando os dados da sua assinatura...</p>
      </div>
    );
  }

  if (status === 'error' || !data) {
    return (
      <div className="min-h-screen bg-[#f6fafe] flex flex-col">
        <div className="w-full bg-[#018ED5] py-3 text-center text-white font-medium text-sm">
          Clubinho Corações Preciosos
        </div>
        <div className="flex-1 flex items-start justify-center px-4 py-10">
          <div className="w-full max-w-md bg-white rounded-2xl border border-[#e6e9f0] p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-[#FDECEC] flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-[#2C2F3A] mb-2">{errorTitle}</h1>
            <p className="text-[#595D69] mb-6">{errorMessage}</p>
            {/* Erro temporário (rede) se resolve tentando de novo; os outros só com link novo. */}
            {!errorRetry && <p className="text-sm text-[#595D69] mb-6">{pecaNovoLink}</p>}
            <div className="flex flex-col gap-3">
              {errorRetry && (
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="w-full h-12 rounded-lg bg-[#018ED5] text-white font-semibold"
                >
                  Tentar novamente
                </button>
              )}
              {inApp && (
                <button
                  type="button"
                  onClick={voltarAoAplicativo}
                  className="w-full h-12 rounded-lg border border-[#018ED5] text-[#018ED5] font-semibold"
                >
                  Voltar ao aplicativo
                </button>
              )}
            </div>
            {inApp && (
              <p className="text-xs text-[#9195A1] mt-4">
                Se a tela não fechar sozinha, use o botão voltar do aplicativo.
              </p>
            )}
          </div>
        </div>
        {!inApp && <Footer />}
      </div>
    );
  }

  // Upgrade de mensal para anual à vista: o ano começa no vencimento da
  // mensal (âncora gravada na aprovação). Só copy — quem decide é o backend.
  const inicioDoAnual =
    isUpgrade && isAnual && data.annualStartsAt
      ? data.annualStartsAt.slice(0, 10).split('-').reverse().join('/')
      : null;
  const upgradeComOferta = isUpgrade && !!data.offerPlan;
  const nomesCriancas = joinNames(children.map((child) => firstName(child.name)));
  const tituloMesmoPlano =
    modoEfetivo === 'retomar'
      ? nomesCriancas
        ? `Retomar a assinatura de ${nomesCriancas}`
        : 'Retomar sua assinatura'
      : 'Renovar plano anual';
  const titulo = isUpgrade
    ? planoSelecionado
      ? `Upgrade para o plano ${planoSelecionado.name}`
      : 'Fazer upgrade de plano'
    : trocouDePlano
      ? `Renovar no plano ${planoSelecionado?.name ?? ''}`.trim()
      : tituloMesmoPlano;
  const subtitulo = isUpgrade
    ? inicioDoAnual
      ? `Com o pagamento aprovado, sua assinatura mensal é encerrada. O mês que você já pagou continua valendo: o plano anual começa em ${inicioDoAnual}, quando esse mês termina, e vale 12 meses a partir daí. A sequência de kits continua do próximo livro, sem voltar ao início.`
      : 'Com o pagamento aprovado, seu plano atual é encerrado e o novo começa na hora, com a cobrança integral do plano escolhido. A sequência de kits continua do próximo livro, sem voltar ao início.'
    : trocouDePlano
      ? 'Você escolheu outro plano: sua assinatura anterior fica encerrada e uma nova começa no plano escolhido. A sequência de kits continua do próximo livro, sem voltar ao início.'
      : modoEfetivo === 'retomar'
        ? 'Sua assinatura continua de onde parou: mesmo histórico e a sequência de kits segue do próximo livro, sem voltar ao início.'
        : 'Seu ciclo anual terminou. A renovação começa um novo ano de assinatura e a sequência de kits continua do próximo livro.';

  return (
    <div className="min-h-screen bg-[#f6fafe] flex flex-col font-lato">
      <div className="w-full bg-[#018ED5] py-3 text-center text-white font-medium text-sm px-4">
        Continue essa jornada de fé com seus filhos
      </div>

      <div className="lg:hidden flex justify-center">
        <img className="h-12 py-2" src="/logo_clubinho_horizontal_v2.png" alt="Clubinho Corações Preciosos" />
      </div>
      <Header />

      <main className="flex-1 w-full flex justify-center px-4 py-6 pb-36 lg:pb-10">
        <div className="w-full max-w-[560px] flex flex-col gap-4">
          <section className="bg-white rounded-2xl border border-[#e6e9f0] p-4 lg:p-6">
            <h1 className="text-xl font-bold text-[#2C2F3A] mb-2">{titulo}</h1>
            <p className="text-[#595D69] text-sm">{subtitulo}</p>

            <div className="mt-4 rounded-xl bg-[#DBECFF] px-4 py-3">
              <p className="text-[#0168AC] font-semibold">{planoSelecionado?.name ?? data.plan.name}</p>
              <p className="text-[#0168AC] text-sm">
                {planoSelecionado ? describePlano(planoSelecionado, !termosNaTela) : 'Plano'} ·{' '}
                {/* No app, valor cobrado: o ano no anual (à vista ou em 12x), o mês nos
                    outros; cupom no cartão do mensal vale só no 1º mês (termos-assinatura).
                    Na web, o anual no cartão pelo valor do mês, como antes. */}
                {pixAVista
                  ? `${formatarReais(precoFinalNumber)} à vista`
                  : isAnual && termosNaTela
                    ? `${formatarReais(precoFinalNumber)} por ano`
                    : cupomSoNoPrimeiroMes
                      ? `${formatPreco(precoMensal)} no 1º mês, depois ${formatPreco(precoTabela)} / mês`
                      : pixSemValorFuturo
                        ? `${formatPreco(precoMensal)} neste PIX`
                        : `${formatPreco(precoMensal)} / mês`}
              </p>
              {isAnual && (
                <p className="text-[#0168AC] text-sm">
                  {pixAVista
                    ? 'Um PIX só, válido por 12 meses'
                    : termosNaTela
                      ? `em 12x de ${formatPreco(precoMensal)} no cartão`
                      : `12x de ${formatPreco(precoMensal)} = ${formatPreco(precoFinalNumber)}`}
                </p>
              )}
              {cupomEfetivo && (
                <p className="text-[#0168AC] text-sm mt-1">
                  Cupom {cupomEfetivo.code} aplicado
                  {cupomEfetivo.discount_type === 'PERCENT'
                    ? ` (-${Number(cupomEfetivo.value) || 0}%)`
                    : ` (-${formatPreco(Number(cupomEfetivo.value) || 0)})`}
                </p>
              )}
              <p className="text-[#0168AC] text-xs mt-1">Frete grátis para todo o Brasil</p>
            </div>

            {/* Cupom travado no link: já aparece como aplicado no resumo
                acima; sem campo para trocar ou remover. */}
            {!isUpgrade && !cupomTravado && (
              <div className="mt-4 flex flex-col gap-1">
                <p className="text-sm font-semibold text-[#2C2F3A]">Cupom de desconto</p>
                <CouponField
                  couponInput={couponInput}
                  couponApplied={coupon}
                  couponError={couponError}
                  couponErrorMessage={couponErrorMessage}
                  onInputChange={(value) => {
                    setCouponInput(value);
                    setCouponError(false);
                  }}
                  onApply={aplicarCupom}
                  onRemove={removerCupom}
                />
              </div>
            )}

            {isUpgrade && (
              <div className="mt-3 rounded-xl border border-[#e6e9f0] px-4 py-3">
                <p className="text-xs text-[#9195A1]">Seu plano atual</p>
                <p className="text-sm font-semibold text-[#2C2F3A]">
                  {data.plan.name} —{' '}
                  {data.plan.billingCycle === 'annual'
                    ? termosNaTela
                      ? `${formatarReais(data.plan.discountedPrice)} /ano`
                      : `${formatPreco(data.plan.discountedPrice / 12)} /mês`
                    : `${formatPreco(data.plan.discountedPrice)} /mês`}
                </p>
              </div>
            )}

            {(isUpgrade ? planos.length > 0 : planos.length > 1) && (
              <div className="mt-4">
                <p className="text-sm font-semibold text-[#2C2F3A] mb-1">
                  {upgradeComOferta
                    ? 'Seu novo plano'
                    : isUpgrade
                      ? 'Escolha seu novo plano'
                      : 'Quer mudar de plano?'}
                </p>
                <p className="text-xs text-[#595D69] mb-3">
                  {upgradeComOferta
                    ? 'O plano que você combinou com a nossa equipe — o preço é o de tabela de hoje e a sequência de kits continua do próximo livro.'
                    : isUpgrade
                      ? 'Planos superiores ao seu plano atual — o preço é o de tabela de hoje e a sequência de kits continua do próximo livro.'
                      : 'Mantenha seu plano ou escolha um superior — o preço é o de tabela de hoje e a sequência de kits continua do próximo livro.'}
                </p>
                <div className="flex flex-col gap-2">
                  {planos.map((plan) => (
                    <PlanOption
                      key={plan.id}
                      plan={plan}
                      selected={plan.id === planId}
                      atual={plan.id === data.plan.id}
                      economia={economias.get(plan.id) ?? null}
                      onSelect={() => setPlanId(plan.id)}
                    />
                  ))}
                </div>
                {isUpgrade ? (
                  <p className="text-xs text-[#595D69] mt-3">
                    {inicioDoAnual
                      ? `Ao confirmar o upgrade, sua assinatura mensal é encerrada e o plano anual é pago agora — cobrança integral do plano escolhido. O ano começa em ${inicioDoAnual}, quando termina o mês que você já pagou.`
                      : 'Ao confirmar o upgrade, sua assinatura atual é encerrada e o novo plano começa com o pagamento aprovado — cobrança integral do plano escolhido.'}
                  </p>
                ) : (
                  trocouDePlano && (
                    <p className="text-xs text-[#595D69] mt-3">
                      Ao trocar de plano, sua assinatura anterior permanece encerrada e o novo
                      plano começa com o pagamento aprovado.
                    </p>
                  )
                )}
              </div>
            )}
          </section>

          <p className="text-sm text-[#595D69] px-1">
            Confira seus dados antes de pagar. Toque em <strong>Editar</strong> se algo mudou.
          </p>

          <SummaryCard
            id="card-responsavel"
            title="Responsável"
            onEdit={openRecipientEdit}
            editing={editing === 'recipient'}
            locked={editing !== null}
            incompleto={!responsavelCompleto}
          >
            {editing === 'recipient' ? (
              <div className="flex flex-col gap-3">
                <Field
                  label="Nome completo"
                  value={recipientDraft.name}
                  autoComplete="name"
                  onChange={(value) => setRecipientDraft((prev) => ({ ...prev, name: value }))}
                />
                <Field
                  label="WhatsApp com DDD"
                  value={recipientDraft.whatsapp}
                  inputMode="tel"
                  type="tel"
                  autoComplete="tel-national"
                  placeholder="(11) 9 8765-4321"
                  maxLength={20}
                  invalid={!!cardError && !!whatsappProblema(recipientDraft.whatsapp)}
                  onChange={(value) =>
                    setRecipientDraft((prev) => ({ ...prev, whatsapp: maskWhatsapp(value) }))
                  }
                />
                {cardError && <p className="text-sm text-red-600">{cardError}</p>}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={saveRecipient}
                    className="flex-1 h-11 rounded-lg bg-[#018ED5] text-white font-semibold"
                  >
                    Salvar
                  </button>
                  <button
                    type="button"
                    onClick={cancelarEdicao}
                    className="flex-1 h-11 rounded-lg border border-[#dadae5] text-[#595D69]"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-[#595D69] text-sm flex flex-col gap-1">
                <p className={nomeOk ? 'text-[#2C2F3A] font-medium' : 'text-red-600 font-medium'}>
                  {recipient.name || 'Nome não informado'}
                </p>
                <p className={problemaWhatsapp ? 'text-red-600' : ''}>
                  {recipient.whatsapp || 'WhatsApp não informado'}
                </p>
                {recipient.whatsapp && problemaWhatsapp && (
                  <p className="text-red-600">{problemaWhatsapp}</p>
                )}
              </div>
            )}
          </SummaryCard>

          <SummaryCard title="E-mail">
            <p className="text-[#2C2F3A] font-medium break-all">{email || 'E-mail não cadastrado'}</p>
            <p className="text-xs text-[#9195A1] mt-1">
              É para este e-mail que enviamos as informações da assinatura. Para trocá-lo, {falarComEquipe}.
            </p>
            {!email && (
              <p className="text-sm text-red-600 mt-2">
                Sua conta está sem e-mail. Para renovar, {falarComEquipe}.
              </p>
            )}
          </SummaryCard>

          <SummaryCard
            id="card-criancas"
            title="Crianças"
            onEdit={children.length > 0 ? openChildrenEdit : undefined}
            editing={editing === 'children' || editing === 'new-child'}
            locked={editing !== null}
            incompleto={children.length === 0}
          >
            {editing === 'children' ? (
              <div className="flex flex-col gap-4">
                {childrenDraft.map((child, index) => (
                  <div key={child.id} className="flex flex-col gap-3 border-b border-[#eef0f5] pb-4 last:border-0">
                    <Field
                      label="Nome da criança"
                      value={child.name}
                      onChange={(value) =>
                        setChildrenDraft((prev) =>
                          prev.map((item, i) => (i === index ? { ...item, name: value } : item)),
                        )
                      }
                    />
                    <Field
                      label="Data de nascimento"
                      type="date"
                      min="2000-01-01"
                      max={HOJE}
                      value={child.birthDate}
                      onChange={(value) =>
                        setChildrenDraft((prev) =>
                          prev.map((item, i) => (i === index ? { ...item, birthDate: value } : item)),
                        )
                      }
                    />
                    <label className="flex flex-col gap-1 w-full">
                      <span className="text-sm text-[#595D69]">Gênero</span>
                      <select
                        className="border border-[#dadae5] rounded-lg h-12 px-4 bg-white"
                        value={child.gender ?? ''}
                        onChange={(event) =>
                          setChildrenDraft((prev) =>
                            prev.map((item, i) =>
                              i === index ? { ...item, gender: event.target.value } : item,
                            ),
                          )
                        }
                      >
                        <option value="">Selecione</option>
                        <option value="Masculino">Masculino</option>
                        <option value="Feminino">Feminino</option>
                      </select>
                    </label>
                  </div>
                ))}
                {cardError && <p className="text-sm text-red-600">{cardError}</p>}
                <div className="flex gap-3">
                  <button
                    type="button"
                    disabled={savingChildren}
                    onClick={saveChildren}
                    className={`flex-1 h-11 rounded-lg text-white font-semibold ${
                      savingChildren ? 'bg-[#dadae5]' : 'bg-[#018ED5]'
                    }`}
                  >
                    {savingChildren ? 'Salvando...' : 'Salvar'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelarEdicao}
                    className="flex-1 h-11 rounded-lg border border-[#dadae5] text-[#595D69]"
                  >
                    Cancelar
                  </button>
                </div>
                <p className="text-xs text-[#9195A1]">
                  Para remover uma criança, {falarComEquipe}.
                </p>
              </div>
            ) : editing === 'new-child' ? (
              <div className="flex flex-col gap-3">
                <Field
                  label="Nome da criança"
                  value={newChild.name}
                  onChange={(value) => setNewChild((prev) => ({ ...prev, name: value }))}
                />
                <Field
                  label="Data de nascimento"
                  type="date"
                  min="2000-01-01"
                  max={HOJE}
                  value={newChild.birthDate}
                  onChange={(value) => setNewChild((prev) => ({ ...prev, birthDate: value }))}
                />
                <label className="flex flex-col gap-1 w-full">
                  <span className="text-sm text-[#595D69]">Gênero</span>
                  <select
                    className="border border-[#dadae5] rounded-lg h-12 px-4 bg-white"
                    value={newChild.gender}
                    onChange={(event) =>
                      setNewChild((prev) => ({ ...prev, gender: event.target.value }))
                    }
                  >
                    <option value="">Selecione</option>
                    <option value="Masculino">Masculino</option>
                    <option value="Feminino">Feminino</option>
                  </select>
                </label>
                {cardError && <p className="text-sm text-red-600">{cardError}</p>}
                <div className="flex gap-3">
                  <button
                    type="button"
                    disabled={savingNewChild}
                    onClick={saveNewChild}
                    className={`flex-1 h-11 rounded-lg text-white font-semibold ${
                      savingNewChild ? 'bg-[#dadae5]' : 'bg-[#018ED5]'
                    }`}
                  >
                    {savingNewChild ? 'Salvando...' : 'Cadastrar criança'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelarEdicao}
                    className="flex-1 h-11 rounded-lg border border-[#dadae5] text-[#595D69]"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {children.length === 0 && suggestedChildren.length === 0 && (
                  <p className="text-sm text-red-600">
                    Não encontramos crianças nesta assinatura. Cadastre abaixo para
                    continuar.
                  </p>
                )}
                {children.map((child) => (
                  <div key={child.id} className="flex items-center justify-between gap-3">
                    <span className="text-[#2C2F3A] font-medium">{child.name}</span>
                    <span className="flex items-center gap-3">
                      <span className="text-sm text-[#595D69]">{formatBirthDate(child.birthDate)}</span>
                      {incluidasDaSugestao.includes(child.id) && editing === null && (
                        <button
                          type="button"
                          onClick={() => undoSuggestedChild(child)}
                          className="text-sm text-[#9195A1] underline py-1"
                        >
                          Remover
                        </button>
                      )}
                    </span>
                  </div>
                ))}

                {suggestedChildren.length > 0 && (
                  <div className="mt-1 rounded-xl bg-[#F5FAFF] border border-[#DBECFF] p-3 flex flex-col gap-2">
                    <p className="text-sm text-[#0168AC]">
                      Encontramos {suggestedChildren.length === 1 ? 'esta criança' : 'estas crianças'}{' '}
                      no seu cadastro. {suggestedChildren.length === 1 ? 'É ela' : 'São elas'} que{' '}
                      {suggestedChildren.length === 1 ? 'recebe' : 'recebem'} os kits desta assinatura?
                    </p>
                    {suggestedChildren.map((child) => (
                      <div key={child.id} className="flex items-center justify-between gap-3">
                        <span className="text-[#2C2F3A] font-medium">
                          {child.name}
                          <span className="text-sm text-[#595D69] font-normal">
                            {' '}
                            · {formatBirthDate(child.birthDate)}
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={() => includeSuggestedChild(child)}
                          disabled={editing !== null}
                          className="text-[#018ED5] text-sm font-semibold whitespace-nowrap py-2 px-1 disabled:opacity-40"
                        >
                          + Incluir
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {editing === null && (
                  <button
                    type="button"
                    onClick={openNewChild}
                    className="self-start text-[#018ED5] text-sm font-semibold mt-1 py-2"
                  >
                    + Adicionar criança
                  </button>
                )}
              </div>
            )}
          </SummaryCard>

          <SummaryCard
            id="card-endereco"
            title="Endereço de entrega"
            onEdit={openShippingEdit}
            editing={editing === 'shipping'}
            locked={editing !== null}
            incompleto={!enderecoCompleto}
          >
            {editing === 'shipping' ? (
              <div className="flex flex-col gap-3">
                <div className="relative">
                  <Field
                    label="CEP"
                    value={shippingDraft.cep}
                    inputMode="numeric"
                    autoComplete="postal-code"
                    maxLength={9}
                    placeholder="00000-000"
                    onChange={(value) =>
                      setShippingDraft((prev) => ({ ...prev, cep: formatCep(value) }))
                    }
                  />
                  {cepLoading && (
                    <svg
                      className="animate-spin h-5 w-5 text-[#018ED5] absolute right-3 top-9"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  )}
                </div>
                {cepNotFound && (
                  <p className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    CEP não encontrado. Preencha o endereço manualmente.
                  </p>
                )}
                <Field
                  label="Rua"
                  autoComplete="address-line1"
                  value={shippingDraft.rua}
                  onChange={(value) => setShippingDraft((prev) => ({ ...prev, rua: value }))}
                />
                <div className="flex gap-3">
                  <Field
                    label="Número"
                    autoComplete="address-line2"
                    value={shippingDraft.numero}
                    onChange={(value) => setShippingDraft((prev) => ({ ...prev, numero: value }))}
                  />
                  <Field
                    label="Complemento"
                    value={shippingDraft.complemento}
                    placeholder="Opcional"
                    onChange={(value) => setShippingDraft((prev) => ({ ...prev, complemento: value }))}
                  />
                </div>
                <Field
                  label="Bairro"
                  value={shippingDraft.bairro}
                  onChange={(value) => setShippingDraft((prev) => ({ ...prev, bairro: value }))}
                />
                <div className="flex gap-3">
                  <Field
                    label="Cidade"
                    value={shippingDraft.cidade}
                    onChange={(value) => setShippingDraft((prev) => ({ ...prev, cidade: value }))}
                  />
                  {/* Select, não texto livre: a AppMax exige a sigla de 2 letras e
                      "São Paulo" ou "S" virava 500 na criação do cliente. */}
                  <label className="flex flex-col gap-1 w-28 shrink-0">
                    <span className="text-sm text-[#595D69]">UF</span>
                    <select
                      className="border border-[#dadae5] rounded-lg h-12 px-3 bg-white"
                      value={UFS.includes(shippingDraft.estado) ? shippingDraft.estado : ''}
                      autoComplete="address-level1"
                      onChange={(event) =>
                        setShippingDraft((prev) => ({ ...prev, estado: event.target.value }))
                      }
                    >
                      <option value="">UF</option>
                      {UFS.map((uf) => (
                        <option key={uf} value={uf}>
                          {uf}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                {cardError && <p className="text-sm text-red-600">{cardError}</p>}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={saveShipping}
                    className="flex-1 h-11 rounded-lg bg-[#018ED5] text-white font-semibold"
                  >
                    Salvar
                  </button>
                  <button
                    type="button"
                    onClick={cancelarEdicao}
                    className="flex-1 h-11 rounded-lg border border-[#dadae5] text-[#595D69]"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-[#595D69] flex flex-col gap-1">
                <p className="text-[#2C2F3A] font-medium">
                  {shipping.rua || 'Endereço não informado'}
                  {shipping.numero ? `, ${shipping.numero}` : ''}
                  {shipping.complemento ? ` — ${shipping.complemento}` : ''}
                </p>
                <p>
                  {[shipping.bairro, shipping.cidade, shipping.estado].filter(Boolean).join(' · ')}
                </p>
                <p>CEP {shipping.cep || '—'}</p>
                {!enderecoCompleto && (shipping.cep || shipping.rua) && (
                  <p className="text-red-600">Falta preencher: {faltasDoEndereco.join(', ')}.</p>
                )}
              </div>
            )}
          </SummaryCard>

          <section className="bg-white rounded-2xl border border-[#e6e9f0] p-4 lg:p-6">
            <h2 className="font-bold text-lg text-[#2C2F3A] mb-1">Pagamento</h2>
            <p className="text-sm text-[#595D69] mb-4">
              {modoEfetivo === 'retomar'
                ? 'Informe os dados de pagamento para reativar sua assinatura.'
                : trocouDePlano
                  ? `Informe os dados de pagamento para começar no plano ${planoSelecionado?.name ?? ''}.`.trim()
                  : 'Informe os dados de pagamento para começar o novo ciclo anual.'}
            </p>

            {!dadosCompletos ? (
              <div className="text-sm text-yellow-900 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="font-semibold mb-2">Para liberar o pagamento, falta:</p>
                <ul className="flex flex-col gap-2">
                  {pendencias.map((pendencia) => (
                    <li key={pendencia.chave} className="flex items-center justify-between gap-3">
                      <span>• {pendencia.texto}</span>
                      {editing === null && (
                        <button
                          type="button"
                          onClick={() => resolverPendencia(pendencia)}
                          className="text-[#018ED5] font-semibold whitespace-nowrap py-1"
                        >
                          Preencher
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <>
                {editing !== null && (
                  <p className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                    Termine de editar seus dados para liberar o pagamento.
                  </p>
                )}
                {/* Continua montado (e inerte) durante a edição: desmontar apagava
                    número, CVV e CPF já digitados. */}
                <div inert={editing !== null} className={editing !== null ? 'opacity-50' : undefined}>
                {allowsPix && (
                  <div className="mb-4">
                    <PaymentMethodSwitch value={paymentMethod} onChange={setPaymentMethod} />
                  </div>
                )}

                {avisoCupom && (
                  <p className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                    {avisoCupom}
                  </p>
                )}

                {requireTurnstile && (
                  <div className="flex flex-col items-center gap-2 mb-2">
                    <TurnstileWidget
                      onVerify={setTurnstileToken}
                      onExpire={() => setTurnstileToken('')}
                      onError={() => setTurnstileToken('')}
                    />
                    {!turnstileToken && (
                      <p className="text-xs text-[#9195A1] text-center">
                        Conclua a verificação de segurança para finalizar o pagamento.
                      </p>
                    )}
                  </div>
                )}

                {/* `key` no planId: trocar de plano remonta o bloco de pagamento.
                    Sem isso um PIX já gerado continuaria na tela — QR do plano
                    antigo debaixo do preço do novo. */}
                {paymentMethod === 'credit-card' ? (
                  <PaymentsCards
                    key={planId}
                    clientId={clientId}
                    planId={planId}
                    coupon={cupomEfetivo}
                    price={{ cupom: !!cupomEfetivo, total: precoFinal, detalhe: detalhePreco }}
                    termos={termos}
                    childIds={children.map((child) => child.id)}
                    recipient={{ name: recipient.name, whatsapp: recipient.whatsapp }}
                    shipping={{ ...shipping, recebedor: recipient.name }}
                    email={email}
                    onEmailChange={setEmail}
                    ensureEmailSaved={async () => clientId}
                    renewal={{
                      token,
                      turnstileToken,
                      requireTurnstile,
                      purpose: data.purpose,
                    }}
                  />
                ) : (
                  <PaymentsPix
                    key={planId}
                    clientId={clientId}
                    planId={planId}
                    coupon={cupomEfetivo}
                    price={{ cupom: !!cupomEfetivo, total: precoFinal, detalhe: detalhePreco }}
                    termos={termos}
                    childIds={children.map((child) => child.id)}
                    recipient={{ name: recipient.name, whatsapp: recipient.whatsapp }}
                    shipping={{ ...shipping, recebedor: recipient.name }}
                    email={email}
                    onEmailChange={setEmail}
                    ensureEmailSaved={async () => clientId}
                    renewal={{ token }}
                  />
                )}
                </div>
              </>
            )}
          </section>

          {inApp && (
            <button
              type="button"
              onClick={voltarAoAplicativo}
              className="text-sm text-[#9195A1] underline self-center py-2"
            >
              Voltar ao aplicativo
            </button>
          )}
        </div>
      </main>

      {!inApp && <Footer />}
    </div>
  );
};

export default Renewal;
